import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { LanguageToggle } from '@/components/ui/LanguageToggle';

export default function TermsOfServicePage() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Navigation */}
      <nav className="relative z-10 border-b border-slate-700/50 bg-slate-900/50 backdrop-blur-sm">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-3">
              <div className="rounded-lg bg-primary-600 p-2">
                <img
                  src="/kernlogic/kernlogic-logo-icon.svg"
                  alt="KernLogic"
                  className="size-6 text-white"
                />
              </div>
              <span className="text-xl font-bold text-white">KernLogic</span>
            </Link>

            <div className="flex items-center space-x-4">
              <LanguageToggle />
              <Button
                asChild
                variant="outline"
                size="sm"
                className="border-slate-600 bg-slate-800/80 text-slate-300 hover:border-slate-500 hover:bg-slate-700/80 hover:text-white"
              >
                <Link to="/request-organization">
                  <ArrowLeft className="mr-2 size-4" />
                  {t('tos.navigation.back')}
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="mx-auto max-w-4xl">
          <div className="rounded-lg border border-slate-700/50 bg-slate-800/90 p-8 shadow-xl backdrop-blur-sm">
            <h1 className="mb-4 text-3xl font-bold text-white">{t('tos.title')}</h1>

            <p className="mb-8 text-sm text-slate-400">{t('tos.effectiveDate')}</p>

            <div className="max-w-none space-y-6">
              <p className="text-slate-200">{t('tos.introduction')}</p>

              {/* Section 1: Order, Acceptance, and Term */}
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-white">{t('tos.sections.1.title')}</h2>

                <div className="space-y-3">
                  <div>
                    <h3 className="font-medium text-white">{t('tos.sections.1.1.title')}</h3>
                    <p className="text-slate-200">{t('tos.sections.1.1.content')}</p>
                  </div>

                  <div>
                    <h3 className="font-medium text-white">{t('tos.sections.1.2.title')}</h3>
                    <p className="text-slate-200">{t('tos.sections.1.2.content')}</p>
                  </div>
                </div>
              </div>

              {/* Section 2: Services and Fees */}
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-white">{t('tos.sections.2.title')}</h2>

                <div className="space-y-3">
                  <div>
                    <h3 className="font-medium text-white">{t('tos.sections.2.1.title')}</h3>
                    <p className="text-slate-200">{t('tos.sections.2.1.content')}</p>
                    <ul className="ml-6 mt-2 list-disc space-y-1 text-slate-200">
                      {(() => {
                        const features = t('tos.sections.2.1.features', {
                          returnObjects: true,
                        });
                        const featureArray = Array.isArray(features) ? features : [];
                        return featureArray.map((feature, index) => <li key={index}>{feature}</li>);
                      })()}
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-medium text-white">{t('tos.sections.2.2.title')}</h3>
                    <p className="text-slate-200">{t('tos.sections.2.2.content')}</p>
                  </div>

                  <div>
                    <h3 className="font-medium text-white">{t('tos.sections.2.3.title')}</h3>
                    <p className="text-slate-200">{t('tos.sections.2.3.content')}</p>
                  </div>

                  <div>
                    <h3 className="font-medium text-white">{t('tos.sections.2.4.title')}</h3>
                    <p className="text-slate-200">{t('tos.sections.2.4.content')}</p>
                  </div>
                </div>
              </div>

              {/* Section 3: Service Levels */}
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-white">{t('tos.sections.3.title')}</h2>

                <div className="space-y-3">
                  <div>
                    <h3 className="font-medium text-white">{t('tos.sections.3.1.title')}</h3>
                    <p className="text-slate-200">{t('tos.sections.3.1.content')}</p>
                  </div>

                  <div>
                    <h3 className="font-medium text-white">{t('tos.sections.3.2.title')}</h3>
                    <p className="text-slate-200">{t('tos.sections.3.2.content')}</p>
                  </div>

                  <div>
                    <h3 className="font-medium text-white">{t('tos.sections.3.3.title')}</h3>
                    <p className="text-slate-200">{t('tos.sections.3.3.content')}</p>
                    <ul className="ml-6 mt-2 list-disc space-y-1 text-slate-200">
                      {(() => {
                        const priorities = t('tos.sections.3.3.priorities', {
                          returnObjects: true,
                        });
                        const priorityArray = Array.isArray(priorities) ? priorities : [];
                        return priorityArray.map((priority, index) => (
                          <li key={index}>{priority}</li>
                        ));
                      })()}
                    </ul>
                  </div>
                </div>
              </div>

              {/* Section 4: Confidentiality and Intellectual Property */}
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-white">{t('tos.sections.4.title')}</h2>

                <div className="space-y-3">
                  <div>
                    <h3 className="font-medium text-white">{t('tos.sections.4.1.title')}</h3>
                    <p className="text-slate-200">{t('tos.sections.4.1.content')}</p>
                  </div>

                  <div>
                    <h3 className="font-medium text-white">{t('tos.sections.4.2.title')}</h3>
                    <p className="text-slate-200">{t('tos.sections.4.2.content')}</p>
                  </div>

                  <div>
                    <h3 className="font-medium text-white">{t('tos.sections.4.3.title')}</h3>
                    <p className="text-slate-200">{t('tos.sections.4.3.content')}</p>
                  </div>
                </div>
              </div>

              {/* Section 5: Data Processing and GDPR */}
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-white">{t('tos.sections.5.title')}</h2>

                <div className="space-y-3">
                  {['5.1', '5.2', '5.3', '5.4', '5.5', '5.6'].map(subsection => (
                    <div key={subsection}>
                      <h3 className="font-medium text-white">
                        {t(`tos.sections.${subsection}.title`)}
                      </h3>
                      <p className="text-slate-200">{t(`tos.sections.${subsection}.content`)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Section 6: Liability and Indemnity */}
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-white">{t('tos.sections.6.title')}</h2>

                <div className="space-y-3">
                  {['6.1', '6.2', '6.3'].map(subsection => (
                    <div key={subsection}>
                      <h3 className="font-medium text-white">
                        {t(`tos.sections.${subsection}.title`)}
                      </h3>
                      <p className="text-slate-200">{t(`tos.sections.${subsection}.content`)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Section 7: Termination */}
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-white">{t('tos.sections.7.title')}</h2>

                <div className="space-y-3">
                  {['7.1', '7.2', '7.3'].map(subsection => (
                    <div key={subsection}>
                      <h3 className="font-medium text-white">
                        {t(`tos.sections.${subsection}.title`)}
                      </h3>
                      <p className="text-slate-200">{t(`tos.sections.${subsection}.content`)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Section 8: Force Majeure */}
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-white">{t('tos.sections.8.title')}</h2>
                <p className="text-slate-200">{t('tos.sections.8.content')}</p>
              </div>

              {/* Section 9: Miscellaneous */}
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-white">{t('tos.sections.9.title')}</h2>

                <div className="space-y-3">
                  {['9.1', '9.2', '9.3', '9.4', '9.5', '9.6'].map(subsection => (
                    <div key={subsection}>
                      <h3 className="font-medium text-white">
                        {t(`tos.sections.${subsection}.title`)}
                      </h3>
                      <p className="text-slate-200">{t(`tos.sections.${subsection}.content`)}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
